
import { defineComponent, PropType } from 'vue'

import AnalyzerListMixin from '~/mixins/analyzerListMixin'
import { Analyzer, AnalyzerConnection, RepositoryKindChoices } from '~/types/types'
import { resolveNodes } from '~/utils/array'
import { fromNow } from '~/utils/date'

type RepoDescConfigT = {
  condition?: boolean
  icon: string
  text: string
  tooltipText?: string
}

export default defineComponent({
  name: 'RepoCard',
  mixins: [AnalyzerListMixin],
  props: {
    removeDefaultStyle: {
      type: Boolean,
      default: false
    },
    size: {
      type: String,
      default: 'base'
    },
    allowStar: {
      type: Boolean,
      default: false
    },
    isStarred: {
      type: Boolean,
      default: false
    },
    id: {
      type: String
    },
    name: {
      type: String,
      required: true
    },
    displayName: {
      type: String,
      required: true
    },
    vcsProvider: {
      type: String,
      required: true
    },
    ownerLogin: {
      type: String
    },
    isPrivate: {
      type: Boolean
    },
    latestCommitOid: {
      type: String,
      default: ''
    },
    defaultBranchName: {
      type: String,
      default: ''
    },
    lastAnalyzedAt: {
      type: String,
      default: ''
    },
    supportedAnalyzers: {
      type: Array as PropType<string[]>,
      default: () => []
    },
    availableAnalyzers: {
      type: Object as PropType<AnalyzerConnection>,
      default: () => ({})
    },
    route: {
      type: String,
      default: ''
    },
    isLink: {
      type: Boolean,
      default: true
    },
    analyzerShortcode: {
      type: String,
      default: ''
    },
    transformerShortcode: {
      type: String,
      default: ''
    },
    showAnalyzers: {
      type: Boolean,
      default: false
    },
    kind: {
      type: String,
      required: true
    },
    showInactiveTag: {
      type: Boolean,
      default: false
    },
    isExactRoute: {
      type: Boolean,
      default: false
    },
    subrepoCount: {
      type: Number,
      default: 0
    },
    createdAt: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      internalStarredState: false
    }
  },
  watch: {
    isStarred: {
      immediate: true,
      handler(newValue): void {
        this.internalStarredState = newValue
      }
    }
  },
  fetch() {
    if (this.showAnalyzers) {
      this.fetchAnalyzers()
    }
  },
  methods: {
    toggleStar(isStarred: boolean): void {
      if (isStarred) {
        this.$emit('star-repo', this.id)
      } else {
        this.$emit('un-star-repo', this.id)
      }
      this.internalStarredState = isStarred
    }
  },
  computed: {
    lastAnalyzedAtFromNow(): string {
      return fromNow(this.lastAnalyzedAt)
    },
    createdAtFromNow(): string {
      return fromNow(this.createdAt)
    },
    repoDescConfig(): RepoDescConfigT[] {
      if (this.kind === RepositoryKindChoices.Monorepo) {
        return [
          {
            condition: Boolean(this.createdAt),
            icon: 'clock',
            text: `Created ${this.createdAtFromNow}`
          },
          {
            condition: Boolean(this.defaultBranchName),
            icon: 'z-git-branch',
            text: this.defaultBranchName,
            tooltipText: 'Default branch name'
          },
          {
            condition: Number.isFinite(this.subrepoCount),
            icon: 'folder-closed',
            text: `${this.subrepoCount} ${
              this.subrepoCount === 1 ? 'sub-repository' : 'sub-repositories'
            }`
          }
        ]
      }

      return [
        {
          condition: Boolean(this.lastAnalyzedAt) || Boolean(this.createdAt),
          icon: 'clock',
          text: this.lastAnalyzedAt
            ? `Analyzed ${this.lastAnalyzedAtFromNow}`
            : `Created ${this.createdAtFromNow}`,
          tooltipText: this.lastAnalyzedAt ? 'Last Analyzed at' : ''
        },
        {
          condition: Boolean(this.defaultBranchName),
          icon: 'z-git-branch',
          text: this.defaultBranchName,
          tooltipText: 'Default branch name'
        },
        {
          condition: Boolean(this.latestCommitOid),
          icon: 'git-commit',
          text: this.latestCommitOid?.slice(0, 7),
          tooltipText: 'Last commit hash'
        }
      ]
    },

    availableAnalyzersArr(): Analyzer[] {
      return this.showAnalyzers ? resolveNodes(this.availableAnalyzers) : []
    },

    analyzerLogos(): Analyzer[] {
      return this.availableAnalyzersArr.length
        ? this.availableAnalyzersArr
        : this.analyzerList.filter((analyzer) =>
            this.supportedAnalyzers.includes(analyzer.shortcode)
          )
    },

    repoRoute(): string {
      if (!this.isLink) {
        return ''
      }

      if (this.isExactRoute) {
        return this.route
      }

      const { provider, owner } = this.$route.params
      const route = ['']

      if (this.vcsProvider) {
        route.push(this.$providerMetaMap[this.vcsProvider].shortcode)
      } else if (provider) {
        route.push(provider)
      }

      if (this.ownerLogin) {
        route.push(this.ownerLogin)
      } else if (owner) {
        route.push(owner)
      }

      route.push(this.name)

      if (this.route) {
        route.push(this.route)
      }

      const routeToRepo = route.join('/')
      let queryParams = ''

      if (this.analyzerShortcode) {
        queryParams = `?preset-analyzer=${this.analyzerShortcode}`
        if (this.transformerShortcode)
          queryParams = queryParams.concat(`&preset-transformer=${this.transformerShortcode}`)
      }

      return routeToRepo.concat(queryParams)
    },
    icon() {
      if (this.isSubRepository) {
        return this.isPrivate ? 'folder-locked' : 'folder-globe'
      }

      if (this.kind === RepositoryKindChoices.Repo) {
        return this.isPrivate ? 'z-lock' : 'globe'
      }

      return 'monorepo'
    },
    isSubRepository(): boolean {
      return this.kind === RepositoryKindChoices.Subrepo
    },
    title(): string {
      return `${this.ownerLogin} / ${this.displayName}`
    }
  }
})
